import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { jobIdOf } from './orgChartPositions';

const CFG = { withCredentials: true };

export function fetchOrgChart({ allCompanies = false } = {}) {
  const qs = allCompanies ? '?companies=all' : '';
  return axios.get(`${API_BASE_URL}/api/org-chart${qs}`, CFG).then((r) => r.data);
}

export function refreshOrgChart() {
  return axios.post(`${API_BASE_URL}/api/admin/org-chart/refresh`, {}, CFG).then((r) => r.data);
}

export function fetchRefreshStatus() {
  return axios.get(`${API_BASE_URL}/api/admin/org-chart/status`, CFG).then((r) => r.data);
}

export function fetchCompanySetting() {
  return axios.get(`${API_BASE_URL}/api/admin/org-chart/companies`, CFG).then((r) => r.data);
}

export function fetchTopOrder() {
  return axios.get(`${API_BASE_URL}/api/admin/org-chart/top-order`, CFG).then((r) => r.data);
}

export function saveTopOrder(jobIds) {
  return axios
    .put(`${API_BASE_URL}/api/admin/org-chart/top-order`, { job_ids: jobIds }, CFG)
    .then((r) => r.data);
}

export function saveCompanySetting(companyIds) {
  return axios
    .put(`${API_BASE_URL}/api/admin/org-chart/companies`, { company_ids: companyIds }, CFG)
    .then((r) => r.data);
}

/**
 * Statuses that carry MEANING get a fixed colour. Everything else is hashed.
 *
 * Hashing alone was wrong: the hash is blind to semantics, so "Active" came out
 * purple while "On Notice" came out the same green a reader takes for healthy,
 * which inverts the signal on exactly the two statuses that most need to read
 * correctly. These four are pinned so green always means active, amber always
 * means probation, and the two exit states always read as warnings.
 */
const SEMANTIC_STATUS_COLOURS = {
  Active: '#2E7D5B',
  Probation: '#C77A2B',
  'On Notice': '#E9453E',
  PIP: '#B02A26',
  'On leave': '#2F6FB0',
};

/**
 * Fallback palette for everything unpinned: free-text plan labels an admin types
 * into Odoo ("TBH", "Vacant 2027"), and any employment status added to the label
 * table after this shipped. Hashing keeps those stable and distinct with no code
 * change. Deliberately excludes the semantic colours above so an unpinned label
 * cannot impersonate one. The palette is the Robusta ink and accent ramp, chosen
 * to stay legible against a white pill.
 */
const STATUS_PALETTE = [
  '#6B7A8F', '#2A8C8C', '#3E9AA0', '#1F3A5F',
  '#4A6488', '#7A5EA8', '#8A6D3B',
];

export function statusColour(label) {
  const s = String(label || '');
  if (SEMANTIC_STATUS_COLOURS[s]) return SEMANTIC_STATUS_COLOURS[s];
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) % 2147483647;
  }
  return STATUS_PALETTE[h % STATUS_PALETTE.length];
}

/**
 * Distinct status values present in the payload, split so the Status dropdown
 * can group employment statuses apart from planning labels. Both are filterable.
 */
export function collectStatuses(nodes) {
  const people = [];
  const planned = [];
  const seenPeople = new Set();
  const seenPlanned = new Set();
  for (const n of nodes) {
    if (!n.status) continue;
    if (n.kind === 'seat') {
      if (!seenPlanned.has(n.status)) { seenPlanned.add(n.status); planned.push(n.status); }
    } else if (!seenPeople.has(n.status)) {
      seenPeople.add(n.status); people.push(n.status);
    }
  }
  return { people, planned };
}

/**
 * Flat node list to a forest. A node whose managerId is absent from the payload
 * is a root: the server promotes out-of-scope parents to null, but a filtered
 * company can also leave a dangling reference, and a missing parent must never
 * drop a node from the render.
 *
 * The server breaks manager cycles before serving, but this runs in the browser
 * render path, so it cannot lean on that remote invariant: every node in a cycle
 * of two or more becomes some other node's child during the linking pass above,
 * so none of them land in roots and the whole fragment would silently vanish
 * from the page. The reachability sweep below promotes any node not reachable
 * from a current root to a root itself, so a stray cycle still renders (as its
 * own disconnected fragment) instead of disappearing with no error.
 *
 * `topOrder` is the admin's chosen sequence for the chart's SECOND level (the
 * jobs reporting to a root) as a list of Odoo job ids. It is applied here, in
 * the one place every surface goes through, so the tree view, the list view,
 * the printable sections and the positions export all agree without each having
 * to re-sort. See orderTopLevel for the ordering rule itself.
 */
export function buildTree(nodes, topOrder = []) {
  const byId = new Map(nodes.map((n) => [n.id, { ...n, children: [] }]));
  const roots = [];
  for (const n of byId.values()) {
    const parent = n.managerId ? byId.get(n.managerId) : null;
    if (parent && parent.id !== n.id) parent.children.push(n);
    else roots.push(n);
  }

  const reachable = new Set();
  const stack = [...roots];
  while (stack.length > 0) {
    const cur = stack.pop();
    if (reachable.has(cur.id)) continue;
    reachable.add(cur.id);
    for (const child of cur.children) stack.push(child);
  }

  // Anything left unreached only got here through a cycle: every member of the
  // cycle is some other member's child, so the group has no root of its own.
  // Promote one node per unreached group to root, and cut the link that made it
  // someone else's child, or the same node would render twice (once as its own
  // root, once still nested under its cycle-mate).
  for (const n of byId.values()) {
    if (reachable.has(n.id)) continue;
    const parent = n.managerId ? byId.get(n.managerId) : null;
    if (parent) {
      const idx = parent.children.indexOf(n);
      if (idx !== -1) parent.children.splice(idx, 1);
    }
    roots.push(n);
    const inner = [n];
    while (inner.length > 0) {
      const cur = inner.pop();
      if (reachable.has(cur.id)) continue;
      reachable.add(cur.id);
      for (const child of cur.children) inner.push(child);
    }
  }

  for (const r of roots) r.children = orderTopLevel(r.children, topOrder);

  return roots;
}

/**
 * Sort one root's direct reports into the admin's configured sequence.
 *
 * Ordering is by Odoo JOB id, not by card id: a job held by two people renders
 * as two cards, and the admin placed the position, not the people. Cards for the
 * same job therefore keep their relative order from the input (a stable sort),
 * which is the server's employee-id order.
 *
 * The job id is read through jobIdOf because the wire spells it `job_id` while
 * the server fixtures and these tests spell it `jobId`. Reading only the
 * camelCase form made every real card rank as unplaced, so the saved order was
 * silently ignored in the browser and the level just came out alphabetical —
 * the same trap orgChartPositions.js documents having hit before.
 *
 * A job the admin has not placed sorts AFTER every placed one, alphabetically by
 * title. That keeps a newly synced C-level role visible and predictable rather
 * than silently first, and means an empty setting yields a fully alphabetical
 * level instead of Odoo's arbitrary id order.
 *
 * Returns the input array untouched when there is nothing to reorder, so an
 * unconfigured chart costs nothing.
 */
export function orderTopLevel(children, topOrder = []) {
  if (!Array.isArray(children) || children.length < 2) return children;

  const rank = new Map();
  (topOrder || []).forEach((jobId, i) => {
    if (!rank.has(jobId)) rank.set(jobId, i);
  });

  const rankOf = (n) => {
    const jobId = jobIdOf(n);
    const r = jobId != null ? rank.get(jobId) : undefined;
    // Unplaced jobs go after every placed one. Infinity rather than a large
    // constant so no configured list can ever be long enough to collide.
    return r === undefined ? Infinity : r;
  };
  const titleOf = (n) => String(n.title || n.name || '');

  return [...children].sort((a, b) => {
    const ra = rankOf(a);
    const rb = rankOf(b);
    if (ra !== rb) return ra - rb;
    // Same rank means either the same job (keep input order, i.e. the server's
    // employee ordering) or two unplaced jobs (alphabetical).
    if (ra === Infinity && jobIdOf(a) !== jobIdOf(b)) {
      return titleOf(a).localeCompare(titleOf(b));
    }
    return 0;
  });
}

/**
 * Narrow a built tree to the nodes that match, plus the managers above them.
 *
 * A branch holding no match is removed outright, so a filter reads as "show me
 * these people" rather than a page of faded cards. The managers above a match
 * are KEPT (the views render them dimmed, since they fail `matches`): dropping
 * them would leave each result floating with no reporting line to place it.
 *
 * Returns new node objects; the input tree is never mutated, because the same
 * `roots` memo is reused as soon as the filter clears.
 */
export function pruneToMatches(roots, matches) {
  const out = [];
  for (const node of roots) {
    const children = pruneToMatches(node.children || [], matches);
    if (children.length > 0 || matches(node)) out.push({ ...node, children });
  }
  return out;
}

/** Ids from a node's parent up to its root, nearest first. Cycle safe. */
export function ancestorIdsOf(nodes, id) {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const out = [];
  const seen = new Set([id]);
  let cur = byId.get(id);
  while (cur && cur.managerId && !seen.has(cur.managerId)) {
    out.push(cur.managerId);
    seen.add(cur.managerId);
    cur = byId.get(cur.managerId);
  }
  return out;
}

export function fetchDirectoryCompanySetting() {
  return axios.get(`${API_BASE_URL}/api/admin/directory/companies`, CFG).then((r) => r.data);
}

export function saveDirectoryCompanySetting(companyIds) {
  return axios
    .put(`${API_BASE_URL}/api/admin/directory/companies`, { company_ids: companyIds }, CFG)
    .then((r) => r.data);
}
